import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogScrollArea,
  DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Input } from '../../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  Copy,
  CheckCircle,
  ExternalLink,
  Code,
  Terminal,
  Settings,
  RefreshCw,
  AlertCircle,
  Eye,
  EyeOff,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getMcpBaseUrl } from '@/config/mcpBaseUrl';
import { apiService } from '@/services/api';
import { buildWizardInstallCommand } from '@/lib/wizardInstallCommand';

export interface BareMCPSuccessIntegration {
  integration_name: string;
  binding_id: string;
  auth_config: {
    api_key?: string;
    description?: string;
  };
  permissions: {
    can_read: boolean;
    can_write: boolean;
    can_search: boolean;
    can_delete: boolean;
  };
  rate_limits: {
    requests_per_hour: number;
  };
  max_content_length: number;
}

export interface BareMCPSuccessModalContentProps {
  onClose: () => void;
  integration: BareMCPSuccessIntegration;
}

function buildHttpConfig(apiKey: string, mcpEndpointUrl: string) {
  return {
    mcpServers: {
      memara: {
        type: 'http',
        url: mcpEndpointUrl,
        headers: {
          Authorization: `Bearer ${apiKey}`,
        },
      },
    },
  };
}

export function BareMCPSuccessModalContent({
  onClose,
  integration: initialIntegration,
}: BareMCPSuccessModalContentProps) {
  const { t } = useTranslation('integrations');
  const [integration, setIntegration] = useState(initialIntegration);
  const [copiedItems, setCopiedItems] = useState<Set<string>>(new Set());
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [regenerateSuccess, setRegenerateSuccess] = useState(false);

  const apiKey = integration.auth_config?.api_key ?? '';
  const hasApiKey = Boolean(apiKey);

  const handleCopy = async (text: string, itemId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems((prev) => new Set([...prev, itemId]));
      setTimeout(() => {
        setCopiedItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }, 2000);
    } catch {
      window.prompt(t('bareMCP.copyFailed'), text);
    }
  };

  const handleRegenerate = async () => {
    const confirmed = window.confirm(t('bareMCP.regenerateConfirm'));
    if (!confirmed) return;

    setIsRegenerating(true);
    try {
      const result = await apiService.regenerateMcpCredentials(integration.binding_id);
      if (result.api_key) {
        setIntegration((prev) => ({
          ...prev,
          auth_config: {
            ...prev.auth_config,
            api_key: result.api_key,
          },
        }));
        setShowApiKey(true);
        setRegenerateSuccess(true);
      } else {
        throw new Error('No API key returned');
      }
    } catch {
      alert(t('bareMCP.regenerateFailed'));
    } finally {
      setIsRegenerating(false);
    }
  };

  const mcpBaseUrl = getMcpBaseUrl();
  const mcpEndpointUrl = `${mcpBaseUrl}/i/${integration.binding_id}/mcp`;
  const mcpDiscoveryUrl = `${mcpBaseUrl}/.well-known/oauth-authorization-server`;

  const wizardCommand = useMemo(
    () => (hasApiKey ? buildWizardInstallCommand(apiKey, integration.binding_id) : ''),
    [apiKey, hasApiKey, integration.binding_id],
  );

  const httpConfigJson = useMemo(
    () =>
      hasApiKey
        ? JSON.stringify(buildHttpConfig(apiKey, mcpEndpointUrl), null, 2)
        : '',
    [apiKey, hasApiKey, mcpEndpointUrl],
  );

  const curlSmokeExample = `# OAuth discovery (no auth)
curl -sfS "${mcpDiscoveryUrl}" | head -c 400

# MCP mount smoke (expect non-5xx; call tools via an MCP client, not REST on this URL)
curl -sS -o /dev/null -w "%{http_code}" -H "Authorization: Bearer ${apiKey}" "${mcpEndpointUrl}" && echo`;

  const stdioConfig = {
    mcpServers: {
      [`memara-${integration.binding_id}`]: {
        command: 'uvx',
        args: [
          '--from',
          'memara-mcp',
          'memara-mcp',
          '--api-key',
          apiKey,
          '--socket-id',
          integration.binding_id,
        ],
        env: {},
      },
    },
  };
  const claudeConfig = JSON.stringify(stdioConfig, null, 2);

  const maskedApiKey = '•'.repeat(Math.min(apiKey.length, 32));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          {t('bareMCP.successTitle')}
        </DialogTitle>
      </DialogHeader>

      <DialogScrollArea className="space-y-6 px-6 py-4">
        {!hasApiKey && (
          <Alert variant="destructive">
            <Settings className="h-4 w-4" />
            <AlertDescription>
              <strong>{t('bareMCP.missingApiKeyTitle')}</strong>
              {' — '}
              {t('bareMCP.missingApiKeyDescription')}
            </AlertDescription>
          </Alert>
        )}

        {regenerateSuccess && (
          <Alert className="border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-800">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription>{t('bareMCP.regenerateSuccess')}</AlertDescription>
          </Alert>
        )}

        {hasApiKey && (
          <>
            <Card className="border-indigo-200 bg-indigo-50/40 dark:bg-indigo-950/20 dark:border-indigo-800">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                  <CardTitle className="text-base">{t('bareMCP.wizard.title')}</CardTitle>
                  <Badge variant="secondary" className="text-xs">
                    {t('bareMCP.wizard.recommendedBadge')}
                  </Badge>
                </div>
                <CardDescription>{t('bareMCP.wizard.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">
                    {t('bareMCP.wizard.commandLabel')}
                  </label>
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-md text-xs overflow-x-auto max-w-full whitespace-pre-wrap break-words">
                    {wizardCommand}
                  </pre>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => void handleCopy(wizardCommand, 'wizard-command')}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {copiedItems.has('wizard-command') ? (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    ) : (
                      <Copy className="h-4 w-4 mr-2" />
                    )}
                    {copiedItems.has('wizard-command')
                      ? t('bareMCP.wizard.copied')
                      : t('bareMCP.wizard.copyWizardCommand')}
                  </Button>
                  <Button variant="outline" onClick={() => { window.location.href = '/wizard'; }}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t('bareMCP.wizard.wizardGuideLink')}
                  </Button>
                </div>
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg dark:bg-orange-950/30 dark:border-orange-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-orange-800 dark:text-orange-200">
                      {t('bareMCP.wizard.securityNote')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{t('bareMCP.wizard.credentialsTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">
                    {t('bareMCP.wizard.bindingIdLabel')}
                  </label>
                  <div className="flex gap-2">
                    <Input readOnly value={integration.binding_id} className="font-mono text-xs" />
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      onClick={() => void handleCopy(integration.binding_id, 'binding-id')}
                      aria-label={t('bareMCP.wizard.copyBindingId')}
                    >
                      {copiedItems.has('binding-id') ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground">
                    {t('bareMCP.wizard.apiKeyLabel')}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={showApiKey ? apiKey : maskedApiKey}
                      className="font-mono text-xs"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      onClick={() => setShowApiKey((prev) => !prev)}
                      aria-label={showApiKey ? t('bareMCP.wizard.hideApiKey') : t('bareMCP.wizard.showApiKey')}
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      onClick={() => void handleCopy(apiKey, 'api-key')}
                      aria-label={t('bareMCP.wizard.copyApiKey')}
                    >
                      {copiedItems.has('api-key') ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        <Alert>
          <Settings className="h-4 w-4" />
          <AlertDescription>{t('bareMCP.successDescription')}</AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('bareMCP.integrationSummary')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('bareMCP.nameLabel')}:</span>
              <span className="font-medium">{integration.integration_name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{t('bareMCP.serverUrlLabel')}:</span>
              <code className="text-xs bg-muted px-2 py-1 rounded break-all text-right max-w-[60%]">
                {mcpEndpointUrl}
              </code>
            </div>
            <div className="pt-2">
              <Button
                variant={hasApiKey ? 'outline' : 'default'}
                size="sm"
                onClick={handleRegenerate}
                disabled={isRegenerating}
              >
                {isRegenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    {t('bareMCP.regenerating')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('bareMCP.regenerateApiKey')}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{t('bareMCP.advancedManualSetup')}</p>
          <Tabs defaultValue="generic_mcp" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="generic_mcp">Generic MCP (HTTP)</TabsTrigger>
              <TabsTrigger value="claude_desktop">Claude Desktop (stdio)</TabsTrigger>
              <TabsTrigger value="curl_examples">cURL Testing</TabsTrigger>
            </TabsList>

            <TabsContent value="generic_mcp" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Code className="h-4 w-4" />
                    Generic MCP Client Setup
                  </CardTitle>
                  <CardDescription>
                    Streamable HTTP configuration for Cursor, Claude Code, and other MCP clients
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!hasApiKey ? (
                    <p className="text-sm text-muted-foreground">
                      {t('bareMCP.missingApiKeyDescription')}
                    </p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Streamable HTTP (remote MCP over HTTP)</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleCopy(httpConfigJson, 'http-config')}
                          >
                            {copiedItems.has('http-config') ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                            Copy Config
                          </Button>
                        </div>
                        <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto max-w-full whitespace-pre-wrap break-words">
                          {httpConfigJson}
                        </pre>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm font-medium">Available Tools:</span>
                          <div className="mt-2 flex flex-wrap gap-1">
                            <Badge variant="outline">store_memory</Badge>
                            <Badge variant="outline">search_memories</Badge>
                            <Badge variant="outline">list_memories</Badge>
                            <Badge variant="outline">get_memory</Badge>
                            <Badge variant="outline">update_memory</Badge>
                            <Badge variant="outline">delete_memory</Badge>
                            <Badge variant="outline">get_server_info</Badge>
                          </div>
                        </div>
                        <div>
                          <span className="text-sm font-medium">Connection Details:</span>
                          <div className="mt-2 space-y-1 text-xs">
                            <div>
                              <strong>Server:</strong> {mcpEndpointUrl}
                            </div>
                            <div>
                              <strong>Binding ID:</strong> {integration.binding_id}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="claude_desktop" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Terminal className="h-4 w-4" />
                    Claude Desktop (stdio) configuration
                  </CardTitle>
                  <CardDescription>
                    Stdio config using the packaged runner. For hosted Claude (claude.ai), use Settings
                    → Connectors with the MCP root URL from your Claude integration (OAuth), not this
                    JSON.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!hasApiKey ? (
                    <p className="text-sm text-muted-foreground">
                      {t('bareMCP.missingApiKeyDescription')}
                    </p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">MCP Configuration</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleCopy(claudeConfig, 'claude-config')}
                          >
                            {copiedItems.has('claude-config') ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                            Copy Config
                          </Button>
                        </div>
                        <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto max-w-full whitespace-pre-wrap break-words">
                          {claudeConfig}
                        </pre>
                      </div>

                      <div className="bg-muted p-4 rounded-md text-sm">
                        <div className="space-y-2">
                          <h4 className="font-medium">Setup Steps:</h4>
                          <ol className="list-decimal list-inside space-y-1 text-sm">
                            <li>
                              <strong>Locate Claude Desktop config file:</strong>
                              <ul className="ml-4 mt-1 space-y-1 text-xs">
                                <li>
                                  macOS:{' '}
                                  <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>
                                </li>
                                <li>
                                  Windows:{' '}
                                  <code>%APPDATA%\Claude\claude_desktop_config.json</code>
                                </li>
                              </ul>
                            </li>
                            <li>
                              <strong>Add the configuration above</strong>
                            </li>
                            <li>
                              <strong>Restart Claude Desktop</strong>
                            </li>
                            <li>
                              <strong>Test</strong> by typing: &quot;Search my memories for...&quot;
                            </li>
                          </ol>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="curl_examples" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Terminal className="h-4 w-4" />
                    cURL smoke checks
                  </CardTitle>
                  <CardDescription>
                    Memory tools are invoked over an MCP session, not REST paths on this URL. Use these
                    commands to verify reachability; use an MCP-capable client to call tools.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Test commands</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleCopy(curlSmokeExample, 'curl-examples')}
                        disabled={!hasApiKey}
                      >
                        {copiedItems.has('curl-examples') ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                        Copy example
                      </Button>
                    </div>
                    <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto max-w-full whitespace-pre-wrap break-words">
                      {curlSmokeExample}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </DialogScrollArea>

      <DialogFooter variant="scrollable" className="gap-2 px-6 pb-6">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button onClick={() => window.open('/integrations/bare-mcp', '_blank')}>
          <ExternalLink className="h-4 w-4 mr-2" />
          View Documentation
        </Button>
      </DialogFooter>
    </div>
  );
}

interface BareMCPSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: BareMCPSuccessIntegration;
}

export function BareMCPSuccessModal({
  isOpen,
  onClose,
  integration,
}: BareMCPSuccessModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent variant="scrollable" className="max-w-5xl">
        <BareMCPSuccessModalContent integration={integration} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
}
